// src/components/AdminPortal/JobPostings.js
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FiEdit, FiInfo, FiTrash2, FiX } from "react-icons/fi";
import AxiosWeb from "../../../utils/axiosWeb";

const typeOptions = [
  { label: "Intern", value: "INTERN" },
  { label: "Full Time", value: "FULL_TIME" },
  { label: "Part Time", value: "PART_TIME" },
  { label: "Contractor", value: "CONTRACTOR" },
];

const statusOptions = [
  { value: "OPEN", label: "Open" },
  { value: "CLOSED", label: "Closed" },
];

const JobPostings = () => {
  const [jobPostings, setJobPostings] = useState([]);
  const [filteredJobPostings, setFilteredJobPostings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [postingToDelete, setPostingToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPosting, setEditingPosting] = useState(null);
  const [editForm, setEditForm] = useState({
    jobTitle: "",
    jobDescription: "",
    department: "",
    location: "",
    type: "INTERN",
    experience: "",
    aboutTheCompany: "",
    role: "",
    whyJoinUs: "",
    numberOfOpening: 1,
    status: "OPEN",
  });

  const postingsPerPage = 5;

  useEffect(() => {
    fetchJobPostings();
  }, []);
const fetchJobPostings = async () => {
  try {
    const response = await AxiosWeb.get(
      `${process.env.REACT_APP_BASE_URL}/jobPosting/getAllJobPostings`
    );

    console.log("Job Posting API Response:", response.data);

    const data = Array.isArray(response.data)
      ? response.data
      : response.data.data ||
        response.data.content ||
        response.data.jobPostings ||
        [];

    const transformedData = data.map((posting) => ({
      id: posting.id || posting._id,
      title: posting.jobTitle,
      department: posting.department,
      location: posting.location,
      type: posting.type,
      experienceFrom: posting.experience
        ? parseInt(posting.experience.split("-")[0])
        : 0,
      experienceTo: posting.experience
        ? parseInt(posting.experience.split("-")[1])
        : 0,
      postedDate: posting.postedOn || posting.postedDate,
      aboutCompany: posting.aboutTheCompany,
      jd: posting.jobDescription,
      role: posting.role,
      applicants: posting.applicants || 0,
      status: posting.status || "OPEN",
      numberOfOpening: posting.numberOfOpening || 1,
      whyJoinUs: posting.whyJoinUs || "",
      deleted: posting.deleted || false,
    }));

    setJobPostings(transformedData);

  } catch (error) {
    console.error("Job Posting Error:", error);
    toast.error("Failed to fetch job postings");
    setJobPostings([]);
  }
};

  // Form validation schema
  const validationSchema = Yup.object({
    jobTitle: Yup.string().required("Job title is required"),
    jobDescription: Yup.string().required("Job description is required"),
    department: Yup.string().required("Department is required"),
    location: Yup.string().required("Location is required"),
    type: Yup.string().required("Type is required"),
    experience: Yup.string().required("Experience is required"),
    aboutTheCompany: Yup.string().required("About the company is required"),
    role: Yup.string().required("Role is required"),
    whyJoinUs: Yup.string().required("Why join us is required"),
    numberOfOpening: Yup.number()
      .required("Required")
      .min(1, "Minimum 1 opening"),
    postedBy: Yup.string().required("Posted by is required"),
  });

  // Formik initialization for Add form
  const formik = useFormik({
    initialValues: {
      jobTitle: "",
      jobDescription: "",
      department: "",
      location: "",
      type: "INTERN",
      experience: "",
      aboutTheCompany: "",
      role: "",
      whyJoinUs: "",
      numberOfOpening: 1,
      status: "OPEN",
      postedBy: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Prepare the data for API call
        const postingData = {
          jobTitle: values.jobTitle,
          jobDescription: values.jobDescription,
          department: values.department,
          location: values.location,
          type: values.type,
          experience: values.experience,
          aboutTheCompany: values.aboutTheCompany,
          role: values.role,
          whyJoinUs: values.whyJoinUs,
          numberOfOpening: Number(values.numberOfOpening),
          status: values.status,
          postedOn: new Date().toISOString().split("T")[0],
          postedBy: values.postedBy,
        };

        // Make API call to create job posting
        await AxiosWeb.post(
          `${process.env.REACT_APP_BASE_URL}/jobPosting/createJobPosting`,
          postingData,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        toast.success(`Job posting "${values.jobTitle}" added successfully!`);
        setShowAddModal(false);
        formik.resetForm();
        fetchJobPostings();
      } catch (error) {
        toast.error(
          `Failed to create job posting: ${
            error.response?.data?.message || error.message
          }`
        );
      }
    },
  });

  // Formik initialization for Edit form

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPosting(null);
    editFormik.resetForm();
  };

  // Edit Formik (Dynamic initial values)
  const editFormik = useFormik({
    initialValues: editingPosting
      ? {
          jobTitle: editingPosting.jobTitle || "",
          jobDescription: editingPosting.jobDescription || "",
          department: editingPosting.department || "",
          location: editingPosting.location || "",
          type: editingPosting.type || "INTERN",
          experience: editingPosting.experience || "",
          aboutTheCompany: editingPosting.aboutTheCompany || "",
          role: editingPosting.role || "",
          whyJoinUs: editingPosting.whyJoinUs || "",
          numberOfOpening: editingPosting.numberOfOpening || 1,
          status: editingPosting.status || "OPEN",
        }
      : {
          jobTitle: "",
          jobDescription: "",
          department: "",
          location: "",
          type: "INTERN",
          experience: "",
          aboutTheCompany: "",
          role: "",
          whyJoinUs: "",
          numberOfOpening: 1,
          status: "OPEN",
        },

    enableReinitialize: true,
    validationSchema,

    onSubmit: async (values) => {
      try {
        await AxiosWeb.put(
          `${process.env.REACT_APP_BASE_URL}/jobPosting/updateJobPosting?id=${editingPosting?.id}`,
          {
            id: editingPosting?.id,
            ...values,
            postedOn: editingPosting?.postedOn,
            postedBy: editingPosting?.postedBy,
          }
        );

        toast.success("Job updated successfully");
        closeModal();
        fetchJobPostings();
      } catch (error) {
        toast.error(error.response?.data?.message || "Update failed");
      }
    },
  });

  // Edit posting - fetch details and open modal
  const handleEditPosting = async (id) => {
    try {
      const response = await AxiosWeb.get(
        `${process.env.REACT_APP_BASE_URL}/jobPosting/getJobPostingById?id=${id}`
      );

      const posting = response.data;
      setEditingPosting(posting);

      // Fill form with fetched data
      setEditForm({
        jobTitle: posting.jobTitle || "",
        jobDescription: posting.jobDescription || "",
        department: posting.department || "",
        location: posting.location || "",
        type: posting.type || "INTERN",
        experience: posting.experience || "",
        aboutTheCompany: posting.aboutTheCompany || "",
        role: posting.role || "",
        whyJoinUs: posting.whyJoinUs || "",
        numberOfOpening: posting.numberOfOpening || 1,
        status: posting.status || "OPEN",
      });

      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch posting", error);
      toast.error("Failed to load job details");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdatePosting = async (e) => {
    e.preventDefault();
    try {
      await AxiosWeb.put(
        `${process.env.REACT_APP_BASE_URL}/jobPosting/updateJobPosting?id=${editingPosting.id}`,
        {
          id: editingPosting.id,
          ...editForm,
          postedOn: editingPosting.postedOn,
          postedBy: editingPosting.postedBy,
        }
      );
      toast.success("Job updated successfully");
      setIsModalOpen(false);
      setEditingPosting(null);
      fetchJobPostings();
    } catch (error) {
      toast.error(error.response?.data?.message || "Update failed");
    }
  };

  useEffect(() => {
    const nonDeletedPostings = jobPostings.filter((jp) => !jp.deleted);

    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const searchedPostings = nonDeletedPostings.filter(
      (jp) =>
        (jp.title || "").toLowerCase().includes(lowerCaseSearchTerm) ||
        (jp.department || "").toLowerCase().includes(lowerCaseSearchTerm) ||
        (jp.location || "").toLowerCase().includes(lowerCaseSearchTerm) ||
        (jp.type || "").toLowerCase().includes(lowerCaseSearchTerm) ||
        (jp.aboutCompany || "").toLowerCase().includes(lowerCaseSearchTerm) ||
        (jp.jd || "").toLowerCase().includes(lowerCaseSearchTerm) ||
        (jp.role || "").toLowerCase().includes(lowerCaseSearchTerm)
    );

    const sortedPostings = searchedPostings.sort(
      (a, b) => new Date(b.postedDate) - new Date(a.postedDate)
    );

    setFilteredJobPostings(sortedPostings);
    setCurrentPage(1);
  }, [jobPostings, searchTerm]);

  // 🔹 Open delete modal instead of window.confirm
  const handleDeletePosting = (id) => {
    const posting = jobPostings.find((jp) => jp.id === id);
    if (posting) {
      setPostingToDelete(posting);
      setShowDeleteModal(true);
    }
  };

  // 🔹 Confirm delete API call
  const confirmDeletePosting = async () => {
    if (!postingToDelete) return;

    try {
      setIsLoading(true);
      await AxiosWeb.delete(
        `${process.env.REACT_APP_BASE_URL}/jobPosting/deleteJobPosting?id=${postingToDelete?.id}`
      );

      toast.success(`Job posting ${postingToDelete.id} deleted successfully.`);
      fetchJobPostings();
    } catch (error) {
      toast.error(
        `Failed to delete job posting: ${
          error.response?.data?.message || error.message
        }`
      );

      // Fallback: Update local state if API fails
      setJobPostings((prevPostings) =>
        prevPostings.map((jp) =>
          jp.id === postingToDelete.id ? { ...jp, deleted: true } : jp
        )
      );
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setPostingToDelete(null);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await AxiosWeb.put(
        `${process.env.REACT_APP_BASE_URL}/jobPosting/updateJobPosting?id=${id}`,
        {
          status: newStatus,
        }
      );

      toast.success(`Job posting ${id} status updated to ${newStatus}.`);
      fetchJobPostings();
    } catch (error) {
      setJobPostings((prevPostings) =>
        prevPostings.map((jp) =>
          jp.id === id ? { ...jp, status: newStatus } : jp
        )
      );
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // More Info
  const handleInfoClick = (posting) => {
    setSelectedPosting(posting);
    setShowInfoModal(true);
  };

  // Pagination logic
  const indexOfLastPosting = currentPage * postingsPerPage;
  const indexOfFirstPosting = indexOfLastPosting - postingsPerPage;
  const currentPostings = filteredJobPostings.slice(
    indexOfFirstPosting,
    indexOfLastPosting
  );

  const totalPages = Math.ceil(filteredJobPostings.length / postingsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div>
      {/* Search Bar Section */}
      <div className="search-bar" style={{ marginBottom: "10px" }}>
        <input
          type="text"
          placeholder="Search..."
          className="search-input"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      <div className="postings-list-section">
        <div className="postings-header">
          <h3>Current Job Postings ({filteredJobPostings?.length} Total)</h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="add-new-record-btn"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="feather feather-plus-circle"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
            Add
          </button>
        </div>
        {filteredJobPostings.length === 0 ? (
          <p className="no-postings">No job postings found.</p>
        ) : (
          <>
            <div className="postings-table-container">
              <table className="postings-table">
                <thead>
                  <tr>
                    <th>Job Title</th>
                    <th>Department</th>
                    <th>Location</th>
                    <th>Type</th>
                    <th>Experience</th>
                    <th>Posted On</th>
                    <th>Status</th>
                    <th>Applicants</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPostings.map((posting) => (
                    <tr key={posting?.id}>
                      <td>{posting?.title}</td>
                      <td
                        style={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          maxWidth: "80px",
                        }}
                      >
                        {posting?.department}
                      </td>
                      <td
                        style={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {posting?.location}
                      </td>
                      <td>{posting?.type}</td>
                      <td>{posting?.experienceFrom} Years</td>
                      <td>
                        {new Date(posting?.postedDate).toLocaleDateString()}
                      </td>
                      <td>
                        <select
                          value={posting.status}
                          onChange={(e) =>
                            handleStatusChange(posting.id, e.target.value)
                          }
                          className={`status-select status-${posting.status.toLowerCase()}`}
                        >
                          {statusOptions.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td style={{ textAlign: "center" }}>
                        {posting.applicants}
                      </td>

                      <td>
                        <button
                          onClick={() => handleInfoClick(posting)}
                          className="action-btn info-btn"
                        >
                          <FiInfo size={18} />
                        </button>

                        <button
                          onClick={() => handleEditPosting(posting.id)}
                          className="action-btn edit-btn"
                        >
                          <FiEdit size={18} />
                        </button>

                        <button
                          onClick={() => handleDeletePosting(posting.id)}
                          className="action-btn delete-btn"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Info Modal */}
      {showInfoModal && selectedPosting && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Job Details : {selectedPosting?.title}</h3>
              <button
                onClick={() => setShowInfoModal(false)}
                className="modal-close-btn"
              >
                <FiX size={24} />
              </button>
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <div
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "10px",
                  backgroundColor: "#f9f9f9",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <strong>Posted By:</strong>
                <div style={{ marginTop: "4px", color: "#333" }}>
                  {selectedPosting?.postedBy || "Admin"}
                </div>
              </div>

              <div
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "10px",
                  backgroundColor: "#f9f9f9",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <strong>About the Company:</strong>
                <div style={{ marginTop: "4px", color: "#333" }}>
                  {selectedPosting?.aboutCompany}
                </div>
              </div>

              <div
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "10px",
                  backgroundColor: "#f9f9f9",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <strong>Role:</strong>
                <div style={{ marginTop: "4px", color: "#333" }}>
                  {selectedPosting?.role}
                </div>
              </div>

              <div
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "10px",
                  backgroundColor: "#f9f9f9",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <strong>Why Join Us:</strong>
                <div style={{ marginTop: "4px", color: "#333" }}>
                  {selectedPosting?.whyJoinUs}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add New Job Posting</h2>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowAddModal(false);
                  formik.resetForm();
                }}
              >
                <FiX size={24} />
              </button>
            </div>
            <form onSubmit={formik.handleSubmit}>
              <div className="form-group">
                <label htmlFor="jobTitle">Job Title:</label>
                <input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  value={formik.values.jobTitle}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.jobTitle && formik.errors.jobTitle ? (
                  <div className="error-message">{formik.errors.jobTitle}</div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="jobDescription">Job Description:</label>
                <textarea
                  id="jobDescription"
                  name="jobDescription"
                  value={formik.values.jobDescription}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows="5"
                  required
                ></textarea>
                {formik.touched.jobDescription &&
                formik.errors.jobDescription ? (
                  <div className="error-message">
                    {formik.errors.jobDescription}
                  </div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="department">Department:</label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formik.values.department}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.department && formik.errors.department ? (
                  <div className="error-message">
                    {formik.errors.department}
                  </div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="location">Location:</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.location && formik.errors.location ? (
                  <div className="error-message">{formik.errors.location}</div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="postedBy">Posted By:</label>
                <input
                  type="text"
                  id="postedBy"
                  name="postedBy"
                  value={formik.values.postedBy}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.postedBy && formik.errors.postedBy ? (
                  <div className="error-message">{formik.errors.postedBy}</div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="type">Type:</label>
                <select
                  id="type"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                >
                  {typeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {formik.touched.type && formik.errors.type ? (
                  <div className="error-message">{formik.errors.type}</div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="experience">Experience (e.g., 2-5):</label>
                <input
                  type="text"
                  id="experience"
                  name="experience"
                  value={formik.values.experience}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="e.g., 2-5"
                  required
                />
                {formik.touched.experience && formik.errors.experience ? (
                  <div className="error-message">
                    {formik.errors.experience}
                  </div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="aboutTheCompany">About the Company:</label>
                <textarea
                  id="aboutTheCompany"
                  name="aboutTheCompany"
                  value={formik.values.aboutTheCompany}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows="3"
                  required
                ></textarea>
                {formik.touched.aboutTheCompany &&
                formik.errors.aboutTheCompany ? (
                  <div className="error-message">
                    {formik.errors.aboutTheCompany}
                  </div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="role">Role:</label>
                <textarea
                  id="role"
                  name="role"
                  value={formik.values.role}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows="3"
                  required
                ></textarea>
                {formik.touched.role && formik.errors.role ? (
                  <div className="error-message">{formik.errors.role}</div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="whyJoinUs">Why Join Us:</label>
                <textarea
                  id="whyJoinUs"
                  name="whyJoinUs"
                  value={formik.values.whyJoinUs}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows="3"
                  required
                ></textarea>
                {formik.touched.whyJoinUs && formik.errors.whyJoinUs ? (
                  <div className="error-message">{formik.errors.whyJoinUs}</div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="numberOfOpening">Number of Openings:</label>
                <input
                  type="number"
                  id="numberOfOpening"
                  name="numberOfOpening"
                  value={formik.values.numberOfOpening}
                  onChange={(e) => {
                    const val = e.target.value;
                    formik.setFieldValue(
                      "numberOfOpening",
                      val === "" ? "" : Number(val)
                    );
                  }}
                  onBlur={formik.handleBlur}
                  min="1"
                  required
                />
                {formik.touched.numberOfOpening &&
                formik.errors.numberOfOpening ? (
                  <div className="error-message">
                    {formik.errors.numberOfOpening}
                  </div>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="status">Status:</label>
                <select
                  id="status"
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="submit" className="modal-save-btn">
                  Add Posting
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    formik.resetForm();
                  }}
                  className="modal-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Job Posting</h2>
              <button onClick={() => setIsModalOpen(false)}>
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdatePosting}>
              <div className="form-group">
                <label>Job Title:</label>
                <input
                  type="text"
                  name="jobTitle"
                  value={editForm.jobTitle}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Job Description:</label>
                <textarea
                  name="jobDescription"
                  rows={5}
                  value={editForm.jobDescription}
                  onChange={handleEditChange}
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Department:</label>
                <input
                  type="text"
                  name="department"
                  value={editForm.department}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Location:</label>
                <input
                  type="text"
                  name="location"
                  value={editForm.location}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Type:</label>
                <select
                  name="type"
                  value={editForm.type}
                  onChange={handleEditChange}
                >
                  {typeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Experience (e.g., 2-5):</label>
                <input
                  type="text"
                  name="experience"
                  value={editForm.experience}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>About the Company:</label>
                <textarea
                  name="aboutTheCompany"
                  rows={3}
                  value={editForm.aboutTheCompany}
                  onChange={handleEditChange}
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Role:</label>
                <textarea
                  name="role"
                  rows={3}
                  value={editForm.role}
                  onChange={handleEditChange}
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Why Join Us:</label>
                <textarea
                  name="whyJoinUs"
                  rows={3}
                  value={editForm.whyJoinUs}
                  onChange={handleEditChange}
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Number of Openings:</label>
                <input
                  type="number"
                  min="1"
                  name="numberOfOpening"
                  value={editForm.numberOfOpening}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Status:</label>
                <select
                  name="status"
                  value={editForm.status}
                  onChange={handleEditChange}
                >
                  {statusOptions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="submit" className="modal-save-btn">
                  Update Posting
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="modal-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 🔹 Delete Confirmation Modal */}
      {showDeleteModal && postingToDelete && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ maxWidth: "400px", textAlign: "center" }}
          >
            <div className="modal-header">
              <h3 style={{ color: "#2a4e85", marginBottom: "15px" }}>
                Confirm Delete
              </h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                <FiX size={24} />
              </button>
            </div>
            <p style={{ color: "#555", marginBottom: "25px" }}>
              Are you sure you want to delete the job posting{" "}
              <strong>"{postingToDelete.title}"</strong>?
            </p>
            <div className="modal-actions" style={{ justifyContent: "center" }}>
              <button
                type="button"
                className="modal-cancel-btn"
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-save-btn"
                style={{ backgroundColor: "#dc3545" }}
                onClick={confirmDeletePosting}
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Add loading style */
        .loading {
          text-align: center;
          padding: 40px;
          font-size: 18px;
          color: var(--subtext);
        }
        
        /* Modal Header Styles */
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 1px solid var(--border);
          padding-bottom: 15px;
        }

        .modal-header h2,
        .modal-header h3 {
          margin: 0;
          color: var(--primary);
        }

        .modal-close-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--subtext);
          padding: 5px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close-btn:hover {
          background-color: var(--border);
          color: var(--text);
        }

        .modal-close-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        /* Rest of the styles remain the same */
        .job-postings-container {
          padding: 20px;
          background-color: var(--bg);
          color: var(--text);
          border-radius: 8px;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border);
          font-family: 'Inter', sans-serif;
          overflow: hidden;
        }

        /* --- Search Bar Styling --- */
        .search-bar-container {
            background-color: white; /* Dark background similar to the image */
            padding: 20px 20px; /* Adjust padding as needed */
            border-radius: 10px; /* Slightly rounded corners */
            margin-bottom: none; /* Space below the search bar */
            display: flex; /* For aligning content */
            align-items: center; /* Vertically center content */
            justify-content: center; /* Horizontally center content */
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08); /* More prominent shadow for the search bar */
            border: 1px solid var(--border); /* Add border to search container */
        }

        .search-input-wrapper {
            display: flex;
            align-items: center;
            background-color: #2a4e85; /* Darker grey for the input wrapper */
            border-radius: 50px; /* Fully rounded shape for the input */
            padding: 10px 20px;
            width: 100%; /* Take full width of its container */
            max-width: 600px; /* Limit maximum width for a cleaner look */
        }

        .search-input-wrapper svg {
            margin-right: 10px;
            color: white; /* Light grey color for the search icon */
        }

        .search-input-wrapper input {
            flex-grow: 1; /* Allows input to take up remaining space */
            border: none;
            background: transparent; /* No background for the input itself */
            color: white; /* Light text color for input */
            font-size: 1em;
            padding: 0;
            outline: none; /* Remove outline on focus */
        }

        .search-input-wrapper input::placeholder {
            color: white; /* Lighter placeholder text */
        }
        /* --- End Search Bar Styling --- */

        /* Job Postings List Section */
        .postings-list-section {
          padding: 25px; /* Added padding back to this section */
          background-color: var(--card); /* Added background back to this section */
          border-radius: 10px;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border);
        }

        .postings-header { /* New style for the header container */
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            border-bottom: 1px solid var(--border);
            padding-bottom: 10px;
        }

        .postings-list-section h3 {
          color: var(--text);
          font-size: 22px;
          margin: 0; /* Remove default margin to align with flexbox */
          padding: 0;
          border-bottom: none; /* Remove individual border as it's on parent .postings-header */
        }

        .add-new-record-btn {
            background-color: #2a4e85; /* Green color for add button */
            color: white;
            border: none;
            border-radius: 6px;
            padding: 8px 12px;
            font-size: 14px;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: background-color 0.3s ease, transform 0.1s ease;
            /* margin-bottom: 20px; Removed, handled by .postings-header */
        }

        .add-new-record-btn:hover {
            background-color: #218838;
            transform: translateY(-1px); /* Slight lift effect on hover */
        }

        .no-postings {
          text-align: center;
          color: var(--subtext);
          font-size: 16px;
          padding: 30px;
        }

        /* Table Styling */
        .postings-table-container {
          overflow-x: auto; /* Enables horizontal scrolling for the table on small screens */
          margin-bottom: 20px;
          border-radius: 8px; /* Rounded corners for the table container */
          border: 1px solid var(--border); /* Border for the table container */
        }

        .postings-table {
          width: 100%; /* Table takes full width of its container */
          border-collapse: collapse; /* Removes spacing between table cells */
          /* min-width: 1200px;  Removed this to remove the horizontal scrollbar */
        }

        .postings-table th,
        .postings-table td {
          padding: 12px 15px;
          border-bottom: 1px solid var(--border);
          text-align: left;
          font-size: 15px;
          color: var(--text);
        }

        .postings-table th {
          background-color: var(--bg); /* Background for table headers */
          font-weight: 600;
          color: #2a4e85;
          text-transform: uppercase;
          font-size: 13px;
        }

        .postings-table tbody tr:hover {
          background-color: var(--bg); /* Subtle highlight on row hover */
        }

        /* Status Select Dropdown */
        .status-select {
          padding: 5px 8px;
          border-radius: 5px;
          border: 1px solid var(--border);
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          background-color: var(--input-bg);
          color: var(--text);
          appearance: none; /* Removes default browser dropdown arrow */
          background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%20viewBox%3D%220%200%20292.4%20292.4%22%3E%3Cpath%20fill%3D%22%23000000%22%20d%3D%22M287%2C197.4L159.9%2C69.8c-3.2-3.2-8.4-3.2-11.6%2C0L5.4%2C197.4c-3.2%2C3.2-3.2%2C8.4%2C0%2C11.6l11.6%2C11.6c3.2%2C3.2%2C8.4%2C3.2%2C11.6%2C0l126.9-126.9c3.2-3.2%2C8.4-3.2%2C11.6%2C0l126.9%2C126.9c3.2%2C3.2%2C8.4-3.2%2C11.6%2C0l11.6-11.6C290.2%2C205.8%2C290.2%2C200.6%2C287%2C197.4z%22%2F%3E%3C%2Fsvg%3E');
          background-repeat: no-repeat;
          background-position: right 8px center;
          background-size: 12px;
          padding-right: 25px; /* Space for the custom arrow */
        }

        /* Status-specific colors for the select dropdown */
        .status-select.status-active {
          background-color: #d4edda; /* Light green */
          color: #155724; /* Darker green text */
          border-color: #c3e6cb;
        }

        .status-select.status-closed {
          background-color: #f8d7da; /* Light red */
          color: #721c24; /* Darker red text */
          border-color: #f5c6cb;
        }

        /* Action Buttons (Edit, Delete) */
        .action-btn {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          margin: 0 5px;
          padding: 5px;
          border-radius: 5px;
          transition: background-color 0.2s ease, color 0.2s ease;
          color: var(--subtext);
        }

        .action-btn:hover {
          background-color: var(--border);
        }

        .action-btn.delete-btn {
          color: var(--error-red);
        }
        .action-btn.delete-btn:hover {
          background-color: rgba(220, 53, 69, 0.1);
        }

        .action-btn.edit-btn {
            color: #2a4e85;
        }
        .action-btn.edit-btn:hover {
            background-color: rgba(0, 123, 255, 0.1);
        }

        /* Pagination */
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 15px;
          margin-top: 20px;
        }

        .pagination button {
          background-color: #2a4e85;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 8px 15px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .pagination button:hover:not(:disabled) {
          background-color: var(--primary-dark);
        }

        .pagination button:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }

        .pagination span {
          font-size: 15px;
          color: var(--text);
        }

        /* Modal Styles (for Add New Job Posting) */
       /* Modal Styles (for Add New Job Posting) */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow-y: auto; /* Allows modal content to scroll if it's too long */
  padding: 20px 0; /* Provides vertical padding for scrollable content */
}

.modal-content {
  background-color: var(--card);
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 600px; /* Increased max-width for more form fields */
  color: var(--text);
  position: relative;
  margin: auto; /* Centers the modal vertically and horizontally */
}

.modal-content h2 {
  margin-top: 0;
  margin-bottom: 25px;
  color: var(--primary);
  font-size: 24px;
  text-align: center;
}

.modal-content .form-group {
  margin-bottom: 15px;
}

.modal-content .form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--subtext);
}

.modal-content .form-group input[type="text"],
.modal-content .form-group input[type="email"],
.modal-content .form-group input[type="date"],
.modal-content .form-group input[type="number"],
.modal-content .form-group textarea,
.modal-content .form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 16px;
  background-color: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
}

.modal-content .form-group textarea {
  min-height: 80px;
  resize: vertical; /* Allows vertical resizing of text areas */
}

.modal-content .form-group input:focus,
.modal-content .form-group select:focus,
.modal-content .form-group textarea:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.modal-save-btn {
  background-color: #2a4e85;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.modal-save-btn:hover {
  background-color: #218838;
}

.modal-cancel-btn {
  background-color: #cccccc;
  color: #333;
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.modal-cancel-btn:hover {
  background-color: #999999;
}


        .form-group-row {
            display: flex,
            gap: 15px,
            flex-wrap: wrap, /* Allows fields in a row to wrap on smaller screens */
        }

        .form-group-row .form-group {
            flex: 1, /* Distributes space evenly among items in the row */
            min-width: 150px, /* Prevents fields from becoming too narrow on larger screens */
        }

        /* Responsive Adjustments (Media Queries) */
        @media (max-width: 768px) {
          .postings-table th, .postings-table td {
            padding: 10px,
            font-size: 14px,
          }
          .postings-table {
            min-width: unset, /* Allow table to shrink on small screens, relying on overflow-x */
          }
          .modal-content {
            padding: 20px,
            max-width: 95%, /* Adjust for very small screens */
          }
          .modal-actions {
            flex-direction: column,
            gap: 10px,
          }
          .modal-save-btn, .modal-cancel-btn {
            width: 100%,
            text-align: center,
          }
          .form-group-row {
            flex-direction: column, /* Stack fields in rows on small screens */
            gap: 0, /* Remove gap when stacked */
          }
          .form-group-row .form-group {
            min-width: unset, /* Remove min-width when stacked */
            width: 100%,
          }
          .postings-header {
            flex-direction: column,
            align-items: flex-start,
          }
          .postings-list-section h3 {
            margin-bottom: 15px, /* Add some space below the title when stacked */
          }
          .add-new-record-btn {
            width: 100%,
            justify-content: center,
          }
        }
      `}</style>
    </div>
  );
};

export default JobPostings;
